import React, { useCallback, useState } from 'react'
import { Field } from 'formik'
import { Box, Button, CardContent, MenuItem } from '@mui/material'
import type { FormikProps } from 'formik'
import * as Yup from 'yup'

import { REGIONS } from 'src/config'
import { EmailField, ProgramIDField, SourceField } from 'src/legacy/components/fields'
import { CustomTextField, GraphQLForm } from 'src/form'
import { InlineForm } from 'src/legacy/components/mui'
import { useAddPatientMutation } from 'src/legacy/lib/graphql/queries.generated'
import { UpdateQueryOptions } from '@apollo/client'
import { checkIfKaiserSource } from 'src/participant/participantUtils'
import { checkIfProductionEnvironment } from 'src/lib/checkIfProductionEnvironment'
import { AutoFillParticipantFormButton } from 'src/legacy/components/addParticipantForms/AutoFillParticipantFormButton'
import { CreateAsInProgramButton } from 'src/legacy/components/addParticipantForms/CreateAsInProgramButton'
import { DevEmailSettingsFields } from 'src/legacy/components/addParticipantForms/DevEmailSettingsFields'

const ParticipantSchema = Yup.object()
  .shape({
    firstname: Yup.string().required(),
    lastname: Yup.string().required(),
    email: Yup.string().email().required(),
    source: Yup.string().required(),
    referralName: Yup.string().required(),
    region: Yup.string()
      .required()
      .default(REGIONS.length === 1 ? REGIONS[0] : null),
    programID: Yup.string().required(),
    memberID: Yup.string().optional(),
  })
  .required()

type ParticipantFormData = Yup.InferType<typeof ParticipantSchema>

type Props = {
  onAddParticipantSuccess?: () => void
}

const ParticipantForm = ({ onAddParticipantSuccess }: Props) => {
  const [addPatientMutation] = useAddPatientMutation({ onCompleted: onAddParticipantSuccess })
  const [devEmailSettingsRefreshKey, setDevEmailSettingsRefreshKey] = useState(0)
  const refreshDevEmailSettings = useCallback(() => setDevEmailSettingsRefreshKey((key) => key + 1), [])

  const handleSubmit = useCallback(
    async (opts: UpdateQueryOptions<ParticipantFormData>): Promise<void> => {
      const { variables } = opts!
      const isKaiserSource = checkIfKaiserSource(variables?.source)
      await addPatientMutation({
        variables: {
          email: variables!.email,
          firstname: variables!.firstname,
          lastname: variables!.lastname,
          programID: variables!.programID,
          region: variables!.region,
          referralName: variables!.referralName,
          source: variables?.source,
          memberID: isKaiserSource || !checkIfProductionEnvironment() ? '00000000' : undefined, // Hardcoded memberID for Kaiser patients as they do not come from dashboard in production (memberID is required by the server if patient is a Kaiser patient)
          phone: checkIfProductionEnvironment() ? undefined : '+18888888888',
          birthDate: checkIfProductionEnvironment() ? undefined : '2000-01-01',
        },
      })
    },
    [addPatientMutation]
  )

  return (
    <GraphQLForm
      onSubmit={handleSubmit}
      ignoredErrors={['DUPLICATE_KEY']}
      validationSchema={ParticipantSchema}
      render={(form: FormikProps<ParticipantFormData>) => (
        <CardContent>
          <InlineForm>
            <Field component={CustomTextField} fullWidth label="First Name" name="firstname" />
            <Field component={CustomTextField} fullWidth label="Last Name" name="lastname" />
            <EmailField />
            <SourceField />
            <Field
              component={CustomTextField}
              fullWidth
              label="Referring HCP"
              name="referralName"
              helperText="Used in welcome email"
            />
            {REGIONS.length > 1 && (
              <Field component={CustomTextField} fullWidth label="Region" name="region" type="select">
                {REGIONS.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Field>
            )}
            <ProgramIDField />
            <Button color="primary" disabled={form.isSubmitting} type="submit" variant="contained">
              Invite
            </Button>
          </InlineForm>
          {!checkIfProductionEnvironment() && (
            <Box display="flex" flexDirection="column" gap={1} mt={2}>
              <DevEmailSettingsFields refreshKey={devEmailSettingsRefreshKey} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                <AutoFillParticipantFormButton form={form} onAutofill={refreshDevEmailSettings} />
                <CreateAsInProgramButton form={form} onAddParticipantSuccess={onAddParticipantSuccess} />
              </Box>
            </Box>
          )}
        </CardContent>
      )}
    />
  )
}

export default ParticipantForm
